/**
 * Per-route SEO metadata -- the single source of truth for titles,
 * descriptions, canonicals, robots directives and breadcrumbs.
 *
 * HOW THIS IS APPLIED
 *
 * Two layers, deliberately:
 *
 *   1. <Seo route="..."/> (src/seo/Seo.jsx) renders the tags through
 *      react-helmet for client-side navigation.
 *   2. scripts/seo-normalize.js applies the same table to the pre-rendered HTML
 *      at build time.
 *
 * Layer 2 is what crawlers actually read, and it guarantees every route is
 * correct without needing all 37 page components edited by hand. Both layers
 * read this file, so they cannot disagree.
 *
 * FIELDS
 *   title        Unique, describes the page, main topic first.
 *   description  Unique. Describes this specific page and its next step.
 *   robots       Omit for normal indexable pages (siteConfig.defaultRobots).
 *   ogType       Defaults to "website".
 *   ogImage      Absolute URL. Defaults to siteConfig.defaultOgImage.
 *   breadcrumbs  [{name, path}]. Home is prepended automatically. Omit on the
 *                homepage -- a single-item breadcrumb is fake hierarchy.
 *
 * NOTE ON SUPERLATIVES: several inherited titles use "Best" / "Top". Those are
 * unverifiable claims and are flagged in SEO_CONTENT_GAPS.md. They are NOT
 * rewritten here, because changing ~20 live titles simultaneously is a genuine
 * ranking risk that the business should stage and measure. Only titles that
 * were duplicated or malformed have been corrected.
 */

const SCHOOL = "School Boards";
const COLLEGE = "Junior College";
const COMPETITIVE = "Competitive Exams";
const BATCHES = "Integrated Batches";
const RESOURCES = "Resources";
const POLICIES = "Policies";

const routeSeo = {
  "/": {
    title:
      "Coaching Classes in Thane for CBSE, ICSE, SSC, NEET & IIT-JEE | Nayak’s Tutorials",
    description:
      "Nayak’s Tutorials, Thane — coaching since 2000 for Std VIII–X (CBSE, ICSE, IGCSE, SSC), XI–XII Science and Commerce, NEET-UG, IIT-JEE, MHT-CET, CA and CS. Enquire about admissions.",
    ogType: "website",
  },

  "/about": {
    title: "About Nayak’s Tutorials — Coaching in Thane Since 2000",
    description:
      "How Nayak’s Tutorials has taught in Thane since 2000: our teaching approach, academic support, 500+ staff and ISO 9001:2015 accreditation across school, junior college and competitive exam programmes.",
    breadcrumbs: [{ name: "About", path: "/about" }],
  },

  "/contact": {
    title: "Contact Nayak’s Tutorials, Thane — Phone, Email & Address",
    description:
      "Reach the Nayak’s Tutorials head office in Thane West. Find our address, phone number, email and location map, or send an admission enquiry directly from this page.",
    breadcrumbs: [{ name: "Contact", path: "/contact" }],
  },

  "/centers": {
    title: "Our Coaching Centres in Thane, Mulund & Mira Road | Nayak’s Tutorials",
    description:
      "Find your nearest Nayak’s Tutorials centre across Thane, Mulund, Mira Road, Bandra, Kalwa and Satara, and see which board and competitive exam programmes each centre offers.",
    breadcrumbs: [{ name: "Centres", path: "/centers" }],
  },

  "/mainfaq": {
    title: "Frequently Asked Questions | Nayak’s Tutorials",
    description:
      "Answers to common questions about admissions timing, which standard to join in, exams covered, progress tracking, fees and refunds at Nayak’s Tutorials, Thane.",
    breadcrumbs: [{ name: "FAQ", path: "/mainfaq" }],
  },

  "/testimonial": {
    title: "Student Results & Testimonials | Nayak’s Tutorials, Thane",
    description:
      "Board results and feedback from Nayak’s Tutorials students and parents, including SSC, CBSE and ICSE toppers from recent academic years.",
    breadcrumbs: [{ name: "Testimonials", path: "/testimonial" }],
  },

  // ---------------------------------------------------------------- school
  "/8th-9th-10th-cbse-icse-ssc-coaching-classes": {
    // Previously shared an identical title AND description with "/" and
    // "/engineering" -- three pages competing on the same metadata.
    title: "Std 8, 9 & 10 Coaching for CBSE, ICSE & SSC in Thane | Nayak’s Tutorials",
    description:
      "Classroom coaching for Std VIII, IX and X across the CBSE, ICSE and SSC boards in Thane. Full syllabus coverage, regular tests and doubt-solving. Enquire about admissions.",
    breadcrumbs: [
      { name: SCHOOL, path: "/8th-9th-10th-cbse-icse-ssc-coaching-classes" },
    ],
  },

  "/cbse-classes": {
    title: "CBSE Coaching Classes for Std 8, 9 & 10 in Thane | Nayak’s Tutorials",
    description:
      "CBSE coaching for Std VIII, IX and X in Thane with subject-wise teaching, detailed notes and regular practice tests aligned to the CBSE curriculum.",
    breadcrumbs: [{ name: SCHOOL, path: "/8th-9th-10th-cbse-icse-ssc-coaching-classes" }, { name: "CBSE", path: "/cbse-classes" }],
  },

  "/icse-classes": {
    title: "ICSE Coaching Classes for Std 8, 9 & 10 in Thane | Nayak’s Tutorials",
    description:
      "ICSE coaching for Std VIII, IX and X in Thane. Focused teaching, targeted study material and regular assessment for the ICSE board syllabus.",
    breadcrumbs: [{ name: SCHOOL, path: "/8th-9th-10th-cbse-icse-ssc-coaching-classes" }, { name: "ICSE", path: "/icse-classes" }],
  },

  "/igcse-classes": {
    title: "IGCSE Coaching Classes for Std 8, 9 & 10 in Thane | Nayak’s Tutorials",
    description:
      "IGCSE coaching for Std VIII, IX and X in Thane, with subject depth and structured practice for the Cambridge IGCSE syllabus.",
    breadcrumbs: [{ name: SCHOOL, path: "/8th-9th-10th-cbse-icse-ssc-coaching-classes" }, { name: "IGCSE", path: "/igcse-classes" }],
  },

  "/classes-for-mh-ssc-board": {
    title: "SSC Board Coaching for Std 8, 9 & 10 in Thane | Nayak’s Tutorials",
    description:
      "Maharashtra SSC board coaching for Std VIII, IX and X in Thane. Complete syllabus coverage, regular tests and board-pattern practice.",
    breadcrumbs: [{ name: SCHOOL, path: "/8th-9th-10th-cbse-icse-ssc-coaching-classes" }, { name: "SSC", path: "/classes-for-mh-ssc-board" }],
  },

  "/class-10-score-booster-course": {
    title: "Class 10 Score Booster & Final Revision Course, Thane | Nayak’s Tutorials",
    description:
      "A focused Std X revision and score-booster programme in Thane covering exam strategy, timed practice and last-mile revision before the board exams.",
    breadcrumbs: [{ name: SCHOOL, path: "/8th-9th-10th-cbse-icse-ssc-coaching-classes" }, { name: "Class 10 Score Booster", path: "/class-10-score-booster-course" }],
  },

  // --------------------------------------------------------------- college
  "/11th-12th-science-coaching-classes-in-thane": {
    title: "XI–XII Science Coaching Classes in Thane | Nayak’s Tutorials",
    description:
      "Std XI and XII Science coaching in Thane covering Physics, Chemistry, Biology and Maths, with board preparation alongside NEET, JEE and MHT-CET readiness.",
    breadcrumbs: [{ name: COLLEGE, path: "/11th-12th-science-coaching-classes-in-thane" }],
  },

  "/11th-12th-commerce-coaching-classes-in-thane": {
    title: "XI–XII Commerce Coaching Classes in Thane | Nayak’s Tutorials",
    description:
      "Std XI and XII Commerce coaching in Thane with simplified concepts and exam-focused practice in Accounts, Economics and related subjects.",
    breadcrumbs: [{ name: COLLEGE, path: "/11th-12th-commerce-coaching-classes-in-thane" }],
  },

  "/tybcom": {
    title: "TY B.Com Coaching Classes in Thane | Nayak’s Tutorials",
    description:
      "TY B.Com coaching in Thane covering Accounts, Finance and allied subjects, with exam-oriented teaching for final-year commerce students.",
    breadcrumbs: [{ name: COLLEGE, path: "/11th-12th-commerce-coaching-classes-in-thane" }, { name: "TY B.Com", path: "/tybcom" }],
  },

  // ----------------------------------------------------------- competitive
  "/neet-coaching-classes-in-thane": {
    title: "NEET-UG Coaching Classes in Thane | Nayak’s Tutorials",
    description:
      "NEET-UG coaching in Thane with expert-led teaching, a structured test series and regularly updated study material for medical entrance aspirants.",
    breadcrumbs: [{ name: COMPETITIVE, path: "/neet-coaching-classes-in-thane" }, { name: "NEET-UG", path: "/neet-coaching-classes-in-thane" }],
  },

  "/iit-jee-classes-in-thane": {
    title: "IIT-JEE Coaching Classes in Thane | Nayak’s Tutorials",
    description:
      "IIT-JEE Main and Advanced coaching in Thane, with concept teaching, rigorous test series and doubt-solving for engineering entrance aspirants.",
    breadcrumbs: [{ name: COMPETITIVE, path: "/iit-jee-classes-in-thane" }, { name: "IIT-JEE", path: "/iit-jee-classes-in-thane" }],
  },

  "/engineering": {
    // Previously carried the homepage's exact title and description.
    title: "Engineering Entrance Coaching — MHT-CET & JEE, Thane | Nayak’s Tutorials",
    description:
      "Engineering entrance preparation in Thane covering MHT-CET and JEE alongside Std XI–XII Science board study, with test practice and doubt-solving support.",
    breadcrumbs: [{ name: COMPETITIVE, path: "/engineering" }, { name: "Engineering Entrance", path: "/engineering" }],
  },

  "/ca": {
    title: "CA & CS Coaching Classes in Thane | Nayak’s Tutorials",
    description:
      "CA and CS coaching in Thane with a structured study path and experienced faculty for students pursuing chartered accountancy and company secretaryship.",
    breadcrumbs: [{ name: COMPETITIVE, path: "/ca" }, { name: "CA & CS", path: "/ca" }],
  },

  "/olympiads": {
    title: "Olympiad Exam Preparation in Thane | Nayak’s Tutorials",
    description:
      "Preparation for national and international Maths and Science Olympiads in Thane, building the critical thinking and problem-solving depth these exams demand.",
    breadcrumbs: [{ name: COMPETITIVE, path: "/olympiads" }, { name: "Olympiads", path: "/olympiads" }],
  },

  "/Nsat": {
    title: "NSAT — Nayak’s Scholarship & Aptitude Test | Nayak’s Tutorials",
    description:
      "Register for NSAT, the Nayak’s Scholarship & Aptitude Test in Thane. Take the test to assess your preparation and qualify for scholarships on our coaching programmes.",
    breadcrumbs: [{ name: "NSAT", path: "/Nsat" }],
  },

  // --------------------------------------------------------------- batches
  "/ace-40-batch-for-iit-jee-integrated": {
    title: "ACE 40 Batch — 2-Year Integrated IIT-JEE Course | Nayak’s Tutorials",
    description:
      "A two-year integrated IIT-JEE Main and Advanced classroom course for Std XI–XII students, combining board study with entrance preparation.",
    breadcrumbs: [{ name: BATCHES, path: "/iit-jee-classes-in-thane" }, { name: "ACE 40 — IIT-JEE", path: "/ace-40-batch-for-iit-jee-integrated" }],
  },

  "/g-10-plus-batch-for-iit-jee-integrated": {
    title: "G-10 Plus Small Batch — Integrated IIT-JEE Course | Nayak’s Tutorials",
    description:
      "A two-year integrated IIT-JEE Main and Advanced course in Thane taught in a small batch format for closer individual attention.",
    breadcrumbs: [{ name: BATCHES, path: "/iit-jee-classes-in-thane" }, { name: "G-10 Plus — IIT-JEE", path: "/g-10-plus-batch-for-iit-jee-integrated" }],
  },

  "/ace-40-batch-for-neet-ug-integrated": {
    title: "ACE 40 Batch — 2-Year Integrated NEET-UG & MHT-CET Course | Nayak’s Tutorials",
    description:
      "A two-year integrated NEET-UG and MHT-CET course for Std XI–XII students, pairing medical entrance preparation with board study.",
    breadcrumbs: [{ name: BATCHES, path: "/neet-coaching-classes-in-thane" }, { name: "ACE 40 — NEET-UG", path: "/ace-40-batch-for-neet-ug-integrated" }],
  },

  "/g-10-plus-batch-for-neet-ug-integrated": {
    title: "G-10 Plus Small Batch — Integrated NEET-UG Course | Nayak’s Tutorials",
    description:
      "A two-year integrated NEET-UG and MHT-CET course taught in a small batch format for personalised guidance through medical entrance preparation.",
    breadcrumbs: [{ name: BATCHES, path: "/neet-coaching-classes-in-thane" }, { name: "G-10 Plus — NEET-UG", path: "/g-10-plus-batch-for-neet-ug-integrated" }],
  },

  "/ace-40-batch-for-mht-cet-engineer": {
    title: "ACE 40 Batch — 2-Year MHT-CET Engineering Course | Nayak’s Tutorials",
    description:
      "A two-year MHT-CET engineering course for Std XI–XII students, preparing for the state entrance exam alongside board examinations.",
    breadcrumbs: [{ name: BATCHES, path: "/engineering" }, { name: "ACE 40 — MHT-CET Engineering", path: "/ace-40-batch-for-mht-cet-engineer" }],
  },

  "/ace-40-batch-for-mht-cet-medical": {
    title: "ACE 40 Batch — 2-Year MHT-CET Medical Course | Nayak’s Tutorials",
    description:
      "A two-year MHT-CET medical course for Std XI–XII students, combining state entrance preparation with Std XI–XII board study.",
    breadcrumbs: [{ name: BATCHES, path: "/neet-coaching-classes-in-thane" }, { name: "ACE 40 — MHT-CET Medical", path: "/ace-40-batch-for-mht-cet-medical" }],
  },

  // ------------------------------------------------------------- resources
  "/blog": {
    title: "Study Tips & Exam Guidance Blog | Nayak’s Tutorials",
    description:
      "Articles from the Nayak’s Tutorials faculty on study techniques, board exam preparation and competitive exam strategy for school and junior college students.",
    breadcrumbs: [{ name: "Blog", path: "/blog" }],
  },

  "/paper": {
    title: "Board Exam Question Papers & Practice Tests | Nayak’s Tutorials",
    description:
      "Download board exam question papers and practice test papers to rehearse the latest exam patterns before your Std X and XII examinations.",
    breadcrumbs: [{ name: RESOURCES, path: "/paper" }, { name: "Question Papers", path: "/paper" }],
  },

  "/timetable": {
    // Previously titled "The Best Coaching Classes in Thane |Nayak,'s Tutorials"
    // -- a malformed brand spelling, with a description copied from the CA page.
    title: "Class Timetable & Batch Schedule | Nayak’s Tutorials, Thane",
    description:
      "Current batch timetables and class schedules for Nayak’s Tutorials programmes across our Thane centres.",
    breadcrumbs: [{ name: RESOURCES, path: "/paper" }, { name: "Timetable", path: "/timetable" }],
  },

  "/notes/matter-in-our-surroundings-class-9-science": {
    title: "Matter in Our Surroundings — Class 9 Science Notes | Nayak’s Tutorials",
    description:
      "Free revision notes for the Class 9 Science chapter “Matter in Our Surroundings”, prepared by the Nayak’s Tutorials faculty.",
    ogType: "article",
    breadcrumbs: [
      { name: RESOURCES, path: "/paper" },
      { name: "Class 9 Science Notes", path: "/notes/matter-in-our-surroundings-class-9-science" },
    ],
  },

  "/preschool": {
    title: "Pre-School & Day Care in Thane — Galloping Minds | Nayak’s Tutorials",
    description:
      "Galloping Minds, the Nayak’s Tutorials pre-school and day care in Thane, offering playgroup and nursery admission with an emphasis on joyful early learning.",
    breadcrumbs: [{ name: "Pre-School", path: "/preschool" }],
  },

  "/career": {
    title: "Careers & Teaching Jobs at Nayak’s Tutorials, Thane",
    description:
      "Teaching and non-teaching career opportunities at Nayak’s Tutorials in Thane. See current openings and how to apply to join our academic team.",
    breadcrumbs: [{ name: "Careers", path: "/career" }],
  },

  "/Anant": {
    title: "AnaNT — Infinite Possibilities, Endless Opportunities | Nayak’s Tutorials",
    description:
      "AnaNT by Nayak’s Tutorials: learn from experienced faculty with comprehensive study material, regular assessment and affordable fees. Partnership opportunities available.",
    breadcrumbs: [{ name: "AnaNT", path: "/Anant" }],
  },

  // -------------------------------------------------------------- policies
  // Canonical policy routes. The legacy misspelled paths 301 to these; see
  // public/.htaccess and src/Route/Route.jsx.
  "/privacy-policy": {
    title: "Privacy Policy | Nayak’s Tutorials",
    description:
      "How Nayak’s Tutorials collects, uses and protects the personal information submitted through our enquiry forms and website.",
    breadcrumbs: [{ name: POLICIES, path: "/privacy-policy" }, { name: "Privacy Policy", path: "/privacy-policy" }],
  },

  "/terms-and-conditions": {
    title: "Terms & Conditions | Nayak’s Tutorials",
    description:
      "The terms and conditions governing your use of the Nayak’s Tutorials website and enrolment in our coaching programmes.",
    breadcrumbs: [{ name: POLICIES, path: "/privacy-policy" }, { name: "Terms & Conditions", path: "/terms-and-conditions" }],
  },

  "/refund-policy": {
    title: "Refund Policy | Nayak’s Tutorials, Thane",
    description:
      "The Nayak’s Tutorials refund policy: when a refund applies, how to request one and what to expect from the process.",
    breadcrumbs: [{ name: POLICIES, path: "/privacy-policy" }, { name: "Refund Policy", path: "/refund-policy" }],
  },

  // ------------------------------------------------------- non-indexable
  // Real pages that must exist but must never compete in search.
  "/thankyou": {
    title: "Thank You | Nayak’s Tutorials",
    description: "Your enquiry has been received. Our team will contact you shortly.",
    robots: "noindex, follow",
  },

  "/404": {
    title: "Page Not Found | Nayak’s Tutorials",
    description:
      "The page you requested could not be found. Browse our coaching programmes, centres or contact the Nayak’s Tutorials team.",
    robots: "noindex, follow",
  },
};

/** Legacy paths kept working via redirect. Never emitted as canonical. */
const legacyRedirects = {
  "/privaypolicy": "/privacy-policy",
  "/termsConditions": "/terms-and-conditions",
  "/refund": "/refund-policy",
};

module.exports = { routeSeo, legacyRedirects };
